import { jsx as _jsx } from "react/jsx-runtime";
import { readableTypeName } from '@iotv/core-services';
import { AdjacentType, Assembler, getIndirectGroupedTypeQuery, getItemTypeToGroupNestedDescendingQueryDef, pluralize, sortByName, } from '@iotv/datamodel';
import { Autocomplete, CircularProgress, FormControl, InputAdornment } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { camelCase_to_Sentence, singularArticle } from '../../helpers/format';
import { AutosizedPopper } from '../common/AutosizedPopper';
import { AutosizedTextField } from '../common/AutosizedTextField';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Queries either the database or (provided) application state relative to a customer or user, respectively, for a given entity type
 * Traverses groups as required
 * Provices a UI element to select an entity
 *
 * @param contextObject either a `contextCustomer` to query relative to, or a `contextUser` to query relative to their allowed groups
 * @param entityType type of the entity to query for
 * @param applicationData only required if passing a `contextUser`, to use for querying
 * @param currentEntity the currently selected entity, should be from `React.useState()`
 * @param setCurrentEntity a function to set the currently selected entity, should be from `React.useState()`
 * @param label a label to apply to the textfield
 * @param fullWidth whether to render to the full width of the parent container, defaults to true
 * @param disabled whether the control is disabled, defaults to false
 * @param required whether the control should hint that a value is required, defaults to false
 * @param clearable whether the value can be cleared, defaults to false
 * @returns Mui {@link Autocomplete}
 */
export const SingleEntityAutoQuery = ({ contextObject, entityType, applicationData, currentEntity, setCurrentEntity, label, fullWidth = true, disabled = false, required = false, clearable = false, }) => {
    const [items, setItems] = useState([]);
    const [querying, setQuerying] = useState(false);
    const query = async () => {
        if (contextObject && entityType) {
            try {
                setQuerying(true);
                if (contextObject.type === 'Customer') {
                    debug &&
                        console.log('SingleEntityAutoQuery querying with Customer', {
                            contextObject,
                        });
                    const query = getItemTypeToGroupNestedDescendingQueryDef({
                        groupDefiningObjectTypeId: 'Customer',
                        supportedType: entityType,
                        includeSupportedTypeInstances: true,
                    });
                    const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                        contextObjects: [contextObject],
                        query,
                    });
                    if (!err && data)
                        setItems(data
                            .getNodesOfType(entityType)
                            .map((zombie) => Assembler.getInstance(zombie))
                            .filter((v) => v !== undefined)
                            .sort(sortByName));
                    setQuerying(false);
                }
                else if (contextObject.type === 'User' && applicationData) {
                    debug &&
                        console.log('SingleEntityAutoQuery querying with User', {
                            contextObject,
                        });
                    const allowedGroupsQuery = getIndirectGroupedTypeQuery({
                        querySubjects: [contextObject],
                        querySubjectType: 'User',
                        allowedGroupSupportedType: entityType,
                    });
                    const allowedGroups = allowedGroupsQuery(applicationData);
                    console.log('SingleEntityAutoQuery allowed groups', allowedGroups);
                    if (allowedGroups) {
                        const response = await ConsumerDao.getGraphQueryResponse({
                            contextObjects: allowedGroups,
                            path: [{ objectTypeId: entityType, adjacencyType: AdjacentType.CHILD }],
                        });
                        if (response.data?.lastNodesAndEdges.nodes) {
                            setItems(response.data?.lastNodesAndEdges.nodes
                                .map((zombie) => Assembler.getInstance(zombie))
                                .filter((v) => v !== undefined)
                                .sort(sortByName));
                        }
                        debug && console.log('SingleEntityAutoQuery query results', response);
                        setQuerying(false);
                    }
                }
            }
            catch (e) {
                debug && console.log('Error in SingleEntityAutoQuery query', e);
                setQuerying(false);
            }
        }
        else {
            setQuerying(false);
        }
    };
    useEffect(() => {
        debug &&
            console.log('SingleEntityAutoQuery used effect on contextObject, entityType', {
                entityType,
            });
        query();
    }, [contextObject, entityType]);
    const autoCompleteLabel = label ?? `${entityType ? readableTypeName(entityType) : 'Entity'} (${querying ? '...' : items.length})`;
    const InputProps = querying
        ? {
            ['InputProps']: {
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
            },
        }
        : undefined;
    return (_jsx(FormControl, { fullWidth: fullWidth, children: _jsx(Autocomplete, { value: currentEntity ?? '',
            id: 'selectEntity',
            size: 'small',
            disabled,
            fullWidth,
            disableClearable: !clearable,
            forcePopupIcon: querying ? false : 'auto',
            loading: querying,
            options: items,
            freeSolo: false,
            getOptionLabel: (option) => option.name ?? '',
            isOptionEqualToValue: (option, value) => value.sk ? option.sk === value.sk : option === value,
            getOptionKey: (option) => `${option.name}${option.sk}`,
            onChange: (_, v) => setCurrentEntity(v),
            slots: { popper: AutosizedPopper },
            renderInput: (params) => {
                return (_jsx(AutosizedTextField, { ...params,
                    ...InputProps,
                    label: autoCompleteLabel,
                    placeholder: querying
                        ? `Loading ${pluralize(camelCase_to_Sentence(entityType ?? 'Entity', false))}`
                        : `Select ${singularArticle(entityType ?? 'Entity')} ${camelCase_to_Sentence(entityType ?? 'Entity', false).toLocaleLowerCase()}`,
                    disabled,
                    error: required && !currentEntity }));
            } }) }));
};
/**
 * Queries either the database or (provided) application state relative to a customer or user, respectively, for a given entity type
 * Traverses groups as required
 * Provices a UI element to select an entity
 *
 * @param contextObject either a `contextCustomer` to query relative to, or a `contextUser` to query relative to their allowed groups
 * @param entityType type of the entity to query for
 * @param applicationData only required if passing a `contextUser`, to use for querying
 * @param currentEntity the currently selected entity, should be from `React.useState()`
 * @param setCurrentEntity a function to set the currently selected entity, should be from `React.useState()`
 * @param onAdd a function that is called when an entity is selected
 * @param onRemove a function that is called when an entity is removed
 * @param label a label to apply to the textfield
 * @param fullWidth whether to render to the full width of the parent container, defaults to true
 * @param disabled whether the control is disabled, defaults to false
 * @param required whether the control should hint that a value is required, defaults to false
 * @param injectedOptions - Additional options to what is queried TODO: sort these, add ability to add custom style to injected options
 * @returns Mui {@link Autocomplete}
 */
export const MultipleEntityAutoQuery = ({ contextObject, entityType, applicationData, currentEntities, setCurrentEntities, onAdd, onRemove, label, fullWidth = true, disabled = false, required = false, injectedOptions = [], }) => {
    const [items, setItems] = useState([]);
    const [querying, setQuerying] = useState(false);
    const query = async () => {
        if (contextObject && entityType) {
            try {
                setQuerying(true);
                if (contextObject.type === 'Customer') {
                    debug &&
                        console.log('MultipleEntityAutoQuery querying with Customer', {
                            contextObject,
                        });
                    const query = getItemTypeToGroupNestedDescendingQueryDef({
                        groupDefiningObjectTypeId: 'Customer',
                        supportedType: entityType,
                        includeSupportedTypeInstances: true,
                    });
                    const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                        contextObjects: [contextObject],
                        query,
                    });
                    if (!err && data)
                        setItems(data
                            .getNodesOfType(entityType)
                            .map((zombie) => Assembler.getInstance(zombie))
                            .filter((v) => v !== undefined)
                            .sort(sortByName));
                    setQuerying(false);
                }
                else if (contextObject.type === 'User' && applicationData) {
                    debug &&
                        console.log('MultipleEntityAutoQuery querying with User', {
                            contextObject,
                        });
                    const allowedGroupsQuery = getIndirectGroupedTypeQuery({
                        querySubjects: [contextObject],
                        querySubjectType: 'User',
                        allowedGroupSupportedType: entityType,
                    });
                    const allowedGroups = allowedGroupsQuery(applicationData);
                    console.log('MultipleEntityAutoQuery allowed groups', allowedGroups);
                    if (allowedGroups) {
                        const response = await ConsumerDao.getGraphQueryResponse({
                            contextObjects: allowedGroups,
                            path: [{ objectTypeId: entityType, adjacencyType: AdjacentType.CHILD }],
                        });
                        if (response.data?.lastNodesAndEdges.nodes) {
                            setItems(response.data?.lastNodesAndEdges.nodes
                                .map((zombie) => Assembler.getInstance(zombie))
                                .filter((v) => v !== undefined)
                                .sort(sortByName));
                        }
                        debug && console.log('MultipleEntityAutoQuery query results', response);
                        setQuerying(false);
                    }
                }
            }
            catch (e) {
                debug && console.log('Error in MultipleEntityAutoQuery query', e);
                setQuerying(false);
            }
        }
        else {
            setQuerying(false);
        }
    };
    useEffect(() => {
        debug &&
            console.log('MultipleEntityAutoQuery used effect on contextObject, entityType', {
                entityType,
            });
        query();
    }, [contextObject, entityType]);
    const autoCompleteLabel = label ?? `${entityType ?? 'Entity'} (${querying ? '...' : items.length})`;
    const InputProps = querying
        ? {
            ['InputProps']: {
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
            },
        }
        : undefined;
    const allOptions = useMemo(() => {
        return items.concat(injectedOptions); // WARN: Injected options are not sorted by name yet. I want to give some more flair for injected options
    }, [injectedOptions, items]);
    return (_jsx(FormControl, { fullWidth: fullWidth, children: _jsx(Autocomplete, { value: currentEntities ?? '',
            id: 'selectEntities',
            size: 'small',
            disabled,
            fullWidth,
            forcePopupIcon: querying ? false : 'auto',
            loading: querying,
            options: allOptions,
            multiple: true,
            disableCloseOnSelect: true,
            freeSolo: false,
            getOptionLabel: (option) => option.name ?? '',
            isOptionEqualToValue: (option, value) => value.sk ? option.sk === value.sk : option === value,
            getOptionKey: (option) => `${option.name}${option.sk}`,
            onChange: (e, v, r, d) => {
                switch (r) {
                    case 'selectOption':
                        d && d.option && onAdd?.(d?.option);
                        break;
                    case 'removeOption':
                        d && d.option && onRemove?.(d?.option);
                        break;
                }
                debug && console.log('MultipleEntityAutoQuery onChange', { e, v, r, d });
                setCurrentEntities(v);
            },
            slots: { popper: AutosizedPopper },
            renderInput: (params) => {
                return (_jsx(AutosizedTextField, { ...params,
                    ...InputProps,
                    label: autoCompleteLabel,
                    placeholder: querying
                        ? `Loading ${pluralize(camelCase_to_Sentence(entityType ?? 'entity', false))}`
                        : `Select several ${pluralize(camelCase_to_Sentence(entityType ?? 'entity', false))}`,
                    disabled,
                    error: required && currentEntities.length === 0 }));
            } }) }));
};
